import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AlertConfig {
  id?: string;
  type: 'error' | 'warning' | 'info' | 'success' | 'confirm';
  title?: string;
  message: string;
  details?: string;
  buttonText?: string;
  confirmText?: string;
  cancelText?: string;
  autoClose?: number; // milliseconds
  showCopyButton?: boolean;
  copyData?: string;
  copyLabel?: string;
  actionButton?: {
    text: string;
    url?: string;
    handler?: () => void;
  };
}

export interface AlertAction {
  id: string;
  action: 'confirm' | 'cancel' | 'close' | 'copy';
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private alertSubject = new BehaviorSubject<AlertConfig | null>(null);
  private actionsSubject = new BehaviorSubject<AlertAction | null>(null);

  alert$ = this.alertSubject.asObservable();
  actions$: Observable<AlertAction | null> = this.actionsSubject.asObservable();

  // Enhanced methods with modern UX
  showError(message: string, title?: string, details?: string): void {
    this.show({ 
      type: 'error', 
      message, 
      title: title || 'เกิดข้อผิดพลาด',
      details
    });
  }

  showWarning(message: string, title?: string): void {
    this.show({ 
      type: 'warning', 
      message, 
      title: title || 'คำเตือน'
    });
  }

  showInfo(message: string, title?: string, autoClose?: number): void {
    this.show({ 
      type: 'info', 
      message, 
      title: title || 'ข้อมูล',
      autoClose
    });
  }

  showSuccess(message: string, title?: string, autoClose: number = 3000): void {
    this.show({ 
      type: 'success', 
      message, 
      title: title || 'สำเร็จ',
      autoClose
    });
  }

  // Success with copy functionality (for URLs, tokens, etc.)
  showSuccessWithCopy(
    message: string, 
    copyData: string, 
    title?: string, 
    copyLabel: string = 'คัดลอก'
  ): void {
    this.show({
      type: 'success',
      title: title || 'สำเร็จ',
      message,
      copyData,
      copyLabel,
      showCopyButton: true
    });
  }

  // Confirmation dialog
  showConfirm(
    message: string, 
    title?: string, 
    confirmText: string = 'ยืนยัน', 
    cancelText: string = 'ยกเลิก'
  ): Promise<boolean> {
    const id = this.generateId();
    
    this.show({
      id,
      type: 'confirm',
      title: title || 'ยืนยันการดำเนินการ',
      message,
      confirmText,
      cancelText
    });

    return new Promise((resolve) => {
      const subscription = this.actions$.subscribe(action => {
        if (action && action.id === id) {
          subscription.unsubscribe();
          resolve(action.action === 'confirm');
        }
      });
    });
  }

  // Show detailed results (for bulk operations)
  showBulkResults(
    title: string, 
    results: any[], 
    successCount: number, 
    failedCount: number
  ): void {
    let message = `สำเร็จ: ${successCount} รายการ\nล้มเหลว: ${failedCount} รายการ`;
    
    let details = '';
    results.forEach((result: any, index: number) => {
      details += `${index + 1}. ${result.fullName || result.name || result.customerCode}: `;
      if (result.success) {
        details += `✅ สำเร็จ\n`;
        if (result.consentUrl) {
          details += `   URL: ${result.consentUrl}\n`;
        }
      } else {
        details += `❌ ล้มเหลว - ${result.error}\n`;
      }
    });

    this.show({
      type: successCount > 0 ? 'success' : 'error',
      title,
      message,
      details
    });
  }

  show(config: AlertConfig): void {
    const id = config.id || this.generateId();
    const alert: AlertConfig = { ...config, id };
    
    this.alertSubject.next(alert);

    // Auto close if specified
    if (alert.autoClose && alert.autoClose > 0) {
      setTimeout(() => {
        this.close();
      }, alert.autoClose);
    }
  }

  close(): void {
    this.alertSubject.next(null);
  }

  // Handle alert actions
  handleAction(action: AlertAction): void {
    this.actionsSubject.next(action);
    
    // Auto close after action (except copy)
    if (action.action !== 'copy') {
      this.close();
    }
  }

  // Copy to clipboard helper
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Failed to copy text:', error);
      return false;
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  // Convenience methods for backward compatibility
  error(message: string, title?: string, details?: string): void {
    this.showError(message, title, details);
  }

  warning(message: string, title?: string): void {
    this.showWarning(message, title);
  }

  info(message: string, title?: string, autoClose?: number): void {
    this.showInfo(message, title, autoClose);
  }

  success(message: string, title?: string, autoClose?: number): void {
    this.showSuccess(message, title, autoClose);
  }
}